import { http } from '@/lib/axios';
import { unwrap, toApiError } from '@/lib/unwrap';
import type { ApiEnvelope } from '@/types/api';

export type EdcResolveOutcome = 'SETTLED' | 'FAILED';

export interface EdcResolveResult {
  id: string;
  state: string;
}

/**
 * Tell the backend what the terminal actually printed.
 *
 * This is called when the terminal never answered and the cashier has read its
 * slip. `SETTLED` requires the 6-digit receipt trace number — not as bookkeeping
 * but as a forcing function: you cannot claim the payment went through without
 * having the receipt in front of you to copy it from.
 */
export async function resolveEdcTransaction(
  paymentTransactionId: string,
  body: { outcome: EdcResolveOutcome; receiptTraceNo?: string; note?: string },
): Promise<EdcResolveResult> {
  try {
    const res = await http.post<ApiEnvelope<EdcResolveResult>>(
      `/admin/edc/transactions/${paymentTransactionId}/resolve`,
      body,
    );
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Stop waiting on a terminal that is still holding the payment.
 *
 * NOT a cancel: the protocol has none, the card is still in the slot, and the
 * customer can still complete the payment. This parks the row and moves the
 * dialog to the resolution prompt, so a cashier whose customer has walked off
 * is not pinned to a spinner for the rest of the response window.
 *
 * It also stops the backend reading the terminal's answer automatically — from
 * here the printed slip is the authority, which is exactly what the prompt then
 * asks for. Idempotent: parking an already-parked row answers 200.
 */
export async function stopWaitingForEdc(
  paymentTransactionId: string,
  note?: string,
): Promise<EdcResolveResult> {
  try {
    const res = await http.post<ApiEnvelope<EdcResolveResult>>(
      `/admin/edc/transactions/${paymentTransactionId}/stop-waiting`,
      { note },
    );
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}
